import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { goi, gio, LoiApi, tien, truocDay } from '../chung/api';
import { useDemSo } from '../chung/dem-so';
import { useHoiLai } from '../chung/hoi-lai';
import { useKetNoi } from '../chung/KetNoi';
import { AnhSanPham, MaDoiChieu } from '../chung/AnhSanPham';
import { HopGhiChu } from '../chung/HopGhiChu';
import { useHep } from '../chung/hep';
import type {
  BangDieuKhien,
  Don,
  NhomSanPham,
  SanPham,
  TrangDon,
  TrangSanPham,
} from '../chung/kieu';

/**
 * Màn hình thu ngân — làm TRƯỚC hai màn kia vì đây là chỗ tiền đi qua.
 *
 * Hai tấm nổi trên máy bàn: hàng đợi bên trái, đơn đang xem bên phải. Thu ngân
 * nhìn hàng đợi cả ca nên nó phải đọc được từ xa — mã đơn to, trạng thái bằng cả
 * chữ lẫn màu, và giỏ treo lâu tự nhấp nháy.
 */
export function ManThuNgan() {
  const [dangXem, datDangXem] = useState<number | null>(null);
  const { song } = useKetNoi();
  const hep = useHep();

  const bang = useHoiLai<BangDieuKhien>('/api/bao-cao/hien-tai');

  /*
    🔴 Lấy MỌI đơn của ngày làm việc, không chỉ giỏ đang mở (đổi 04/09).

       Cột trái giờ là "đơn hàng hôm nay": đang thêm ở trên, hoàn thành ở dưới.
       Thu ngân xác nhận xong vẫn thấy đơn nằm đó — trước đây nó biến mất khỏi
       danh sách ngay, nên muốn kiểm lại một đơn vừa thu là không còn đường nào.

       Ngày lấy từ bảng điều khiển chứ không tự tính trong trình duyệt: máy thu
       ngân đặt sai múi giờ thì `new Date()` ra ngày khác với ngày làm việc mà
       máy chủ đang dùng, và cả màn hình lệch một ngày mà không có gì báo.
  */
  const ngay = bang.duLieu?.ngay;
  const hangDoi = useHoiLai<TrangDon>(ngay ? `/api/don?ngay=${ngay}` : null);
  const don = useHoiLai<Don>(dangXem ? `/api/don/${dangXem}` : null);

  const ds = hangDoi.duLieu?.danh_sach ?? [];

  useEffect(() => {
    if (dangXem && !hangDoi.dangTai && ds.length > 0 && !ds.some((d) => d.id === dangXem)) {
      datDangXem(null);
    }
  }, [ds, dangXem, hangDoi.dangTai]);

  const taiLaiHet = () => {
    hangDoi.taiLai();
    bang.taiLai();
    don.taiLai();
  };

  /*
    Điện thoại (14/09): HAI MÀN nối tiếp thay vì hai tấm cạnh nhau. Chưa chọn đơn
    → cả màn là hàng đợi; chọn rồi → cả màn là chi tiết, có nút quay lại. Cùng
    hai thành phần, chỉ khác cách xếp — xem chú thích `useHep`.
  */
  if (hep) {
    return (
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {dangXem === null ? (
          <HangDoi
            ds={ds}
            dangXem={dangXem}
            chon={datDangXem}
            mocTreo={bang.duLieu?.moc_treo_lau_phut ?? 180}
            dangTai={hangDoi.dangTai}
            hep
          />
        ) : (
          <ChiTiet don={don.duLieu} song={song} xong={taiLaiHet} hep quayLai={() => datDangXem(null)} />
        )}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', gap: 14, minHeight: 0 }}>
      <HangDoi
        ds={ds}
        dangXem={dangXem}
        chon={datDangXem}
        mocTreo={bang.duLieu?.moc_treo_lau_phut ?? 180}
        dangTai={hangDoi.dangTai}
      />
      <ChiTiet don={don.duLieu} song={song} xong={taiLaiHet} />
    </div>
  );
}

// ───────────────────────── số của hôm nay ─────────────────────────

/**
 * Dải số của hôm nay, đặt giữa thanh trên — số của CẢ CỬA HÀNG.
 *
 * 🔴 Đổi 23/09, theo yêu cầu cửa hàng sau một tuần chạy thật: bốn con số của
 *    ngày làm việc — giỏ hoàn thành, sản phẩm bán được, doanh thu, giỏ treo lâu.
 *
 *    Thành tích riêng của người đang đăng nhập (có từ 04/09) rời khỏi dải này.
 *    Nhân viên tư vấn vẫn thấy thành tích của mình ở `ThanhTichNho`; thu ngân
 *    trên màn hẹp cũng vậy.
 *
 *    Cả bốn số đều lấy từ MỘT lần gọi `/api/bao-cao/hien-tai`, nên chúng luôn
 *    nói về cùng một thời điểm — hai nguồn hỏi lại theo hai nhịp khác nhau thì
 *    có lúc "giỏ hoàn thành" đã nhảy mà "doanh thu" chưa.
 */
export function SoHomNay() {
  const { duLieu } = useHoiLai<BangDieuKhien>('/api/bao-cao/hien-tai');
  if (!duLieu) {
    return (
      <div style={{ display: 'flex', gap: 30 }}>
        {[80, 90, 108].map((w, i) => (
          <div key={i}>
            <div className="xuong" style={{ width: w * 0.7, height: 9, marginBottom: 7 }} />
            <div className="xuong" style={{ width: w, height: 16 }} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 30, alignItems: 'center' }}>
      <O nhan="Giỏ hoàn thành" so={duLieu.don_da_thu_hom_nay} />
      <O nhan="Sản phẩm bán được" so={duLieu.so_mon_ban_hom_nay} mau="var(--nhan)" />
      <O nhan="Doanh thu" so={duLieu.doanh_thu_hom_nay} laTien mau="var(--tot)" />
      {/* Chỉ giỏ mở HÔM NAY quá 3 giờ — máy chủ đã lọc theo ngày (23/09). */}
      {duLieu.gio_treo_lau > 0 && (
        <O nhan="Giỏ treo lâu" so={duLieu.gio_treo_lau} mau="var(--cho)" />
      )}
    </div>
  );
}

/**
 * Một ô thống kê. Số CHẠY khi đổi — thu ngân đang nhìn chỗ khác vẫn bắt được
 * chuyển động ở khoé mắt và biết vừa có đơn mới.
 *
 * (Chỉ ở đây thôi. Con số "khách phải trả" thì tuyệt đối không chạy — xem
 * chú thích trong `dem-so.ts`.)
 *
 * Không bọc chiều sâu quanh mấy ô này: chúng đã nằm trong thanh trên vốn đã nổi,
 * và nổi-trong-nổi-trong-nổi thì mặt phẳng bẩn đi, mắt hết đọc ra thứ tự.
 */
function O({
  nhan,
  so,
  laTien,
  mau,
}: {
  nhan: string;
  so: number;
  laTien?: boolean;
  mau?: string;
}) {
  const hien = useDemSo(so);
  return (
    <div className="hien-mo">
      <div className="nhan-chu">{nhan}</div>
      <div className="so" style={{ fontSize: 18, fontWeight: 700, color: mau, marginTop: 2 }}>
        {laTien ? tien(hien) : hien}
      </div>
    </div>
  );
}

// ───────────────────────── hàng đợi ─────────────────────────

function HangDoi({
  ds,
  dangXem,
  chon,
  mocTreo,
  dangTai,
  hep = false,
}: {
  ds: Don[];
  dangXem: number | null;
  chon: (id: number) => void;
  mocTreo: number;
  dangTai: boolean;
  /** Điện thoại: chiếm cả bề ngang, không cố định 396px. */
  hep?: boolean;
}) {
  /*
    Ô tìm đơn (14/09): cuối ca cột trái dài vài chục đơn, thu ngân cần "đơn 0658
    lúc nãy" hoặc "đơn có ghi chú gói quà" mà không cuộn. Lọc ngay trên máy —
    danh sách đã tải sẵn cả ngày, không gọi thêm máy chủ. Khớp: mã khách, tên
    người mở, ghi chú, tên/mã sản phẩm trong giỏ. Không dấu cũng ra.
  */
  const [tim, datTim] = useState('');
  const khong_dau = (s: string) =>
    s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
  const t = khong_dau(tim.trim());
  const khop = (d: Don) =>
    !t ||
    d.ma.includes(t) ||
    khong_dau(d.nguoi_tao.ho_ten).includes(t) ||
    khong_dau(d.ghi_chu ?? '').includes(t) ||
    d.dong.some((x) => khong_dau(x.ten).includes(t) || x.sku.toLowerCase().includes(t));
  const dsLoc = ds.filter(khop);
  const dangThem = dsLoc.filter((d) => d.trang_thai === 'DANG_MUA');
  const daXong = dsLoc.filter((d) => d.trang_thai !== 'DANG_MUA');

  return (
    <div
      className="noi troi-len"
      style={{
        width: hep ? '100%' : 396,
        minWidth: 0,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '18px 24px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexShrink: 0,
        }}
      >
        <span className="nhan-chu">Đơn hàng</span>
        {/*
          "1/5" = đang thêm / tổng đơn trong ngày. Hai con số cạnh nhau nói được
          thứ một con số không nói được: còn mấy khách đang chọn, trên tổng bao
          nhiêu khách đã qua quầy hôm nay.
        */}
        <span className="so" style={{ fontWeight: 700, fontSize: 16 }}>
          <span style={{ color: dangThem.length > 0 ? 'var(--nhan)' : undefined }}>
            {dangThem.length}
          </span>
          <span style={{ color: 'var(--chu-3)' }}>/{ds.length}</span>
        </span>
      </div>

      <div style={{ padding: '0 18px 10px', flexShrink: 0 }}>
        <input
          value={tim}
          onChange={(e) => datTim(e.target.value)}
          placeholder="Tìm đơn: mã khách, ghi chú, tên hàng…"
          spellCheck={false}
          style={{ padding: '11px 14px', fontSize: 'var(--chu-nho)' }}
          aria-label="Tìm đơn hàng"
        />
      </div>

      {/* Lề 18px quanh vùng cuộn: bóng của mỗi dòng trải ra ~19px, dán sát mép
          thì bị cắt cụt và dòng trông như dán chứ không như nổi. */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 18px 18px' }}>
        {!dangTai && ds.length > 0 && dsLoc.length === 0 && (
          <div className="hien-len" style={{ padding: '36px 20px', textAlign: 'center', color: 'var(--chu-3)' }}>
            Không đơn nào khớp "{tim.trim()}"
          </div>
        )}
        {dangTai && ds.length === 0 && (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="xuong" style={{ height: 74, marginBottom: 12 }} />
            ))}
          </>
        )}
        {!dangTai && ds.length === 0 && (
          <div
            className="hien-len"
            style={{ padding: '48px 20px', textAlign: 'center', color: 'var(--chu-3)' }}
          >
            Chưa có đơn nào hôm nay
          </div>
        )}

        {dangThem.map((d, i) => (
          <DongHangDoi
            key={d.id}
            d={d}
            thuTu={i}
            chon={dangXem === d.id}
            mocTreo={mocTreo}
            bam={() => chon(d.id)}
          />
        ))}

        {/* Đơn đã đóng nằm DƯỚI và có đầu đề riêng. Trộn lẫn thì cột trái dài
            dần suốt ca và thu ngân phải đọc trạng thái từng dòng mới biết cái
            nào còn phải làm. */}
        {daXong.length > 0 && (
          <div className="nhan-chu" style={{ margin: '18px 4px 12px', color: 'var(--chu-3)' }}>
            Đã đóng · {daXong.length}
          </div>
        )}
        {daXong.map((d, i) => (
          <DongHangDoi
            key={d.id}
            d={d}
            thuTu={i}
            chon={dangXem === d.id}
            mocTreo={mocTreo}
            bam={() => chon(d.id)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Một giỏ trong hàng đợi.
 *
 * 🔴 Dòng đang chọn thì LÕM XUỐNG, các dòng khác nổi lên.
 *
 *    Đây là cách phong cách này nói "cái này đang được chọn": vật bị ấn xuống
 *    mặt bàn. Mạnh hơn hẳn đổi màu nền, và không tốn thêm màu nào — mà cả màn
 *    hình này chỉ được phép có một màu.
 */
function DongHangDoi({
  d,
  thuTu,
  chon,
  mocTreo,
  bam,
}: {
  d: Don;
  thuTu: number;
  chon: boolean;
  mocTreo: number;
  bam: () => void;
}) {
  /*
    🔴 Chỉ đơn CÒN MỞ mới treo lâu được. Đơn đã xác nhận hay đã huỷ thì xong rồi,
       gắn "treo lâu" lên nó là báo động về một việc không còn phải làm — và một
       cảnh báo không hành động được thì lần sau người ta bỏ qua luôn cả cái thật.

    Đếm từ LÚC MỞ GIỎ (đổi 04/09), khớp với `GIO_TREO_LAU` ở backend.
  */
  const treoLau =
    d.trang_thai === 'DANG_MUA' &&
    Date.now() - new Date(d.tao_luc).getTime() > mocTreo * 60000;
  const dangThem = d.trang_thai === 'DANG_MUA';

  return (
    <button
      onClick={bam}
      className={`truot-phai ${chon ? 'lom-nho' : 'noi-nho'}`}
      style={{
        // Các dòng nổi lên lệch nhau một nhịp rất ngắn. Chặn ở dòng thứ tám để
        // hàng đợi dài không phải chờ cả giây mới hiện xong.
        animationDelay: `${Math.min(thuTu, 8) * 45}ms`,
        width: '100%',
        textAlign: 'left',
        display: 'block',
        padding: '15px 18px',
        marginBottom: 12,
      }}
    >
      {/*
        🔴 Tiền nằm ở DÒNG DƯỚI, không nằm cạnh mấy cái thẻ.

           Bản trước để mã · thẻ · tiền chung một dòng. Giỏ nào VỪA chờ thu tiền
           VỪA treo lâu thì có hai thẻ, và dòng tràn 13px — **số tiền bị cắt
           mất**. Đo tại chỗ mới thấy, nhìn lướt tưởng vừa khít.

           Và đó đúng là dòng thu ngân cần đọc nhất. Nên tách ra: thẻ được phép
           xuống dòng, tiền có chỗ riêng và không bao giờ co lại.
      */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          flexWrap: 'wrap',
          marginBottom: 8,
        }}
      >
        <span
          className="so"
          style={{
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: '0.02em',
            color: chon ? 'var(--nhan)' : 'var(--chu)',
            transition: 'color var(--vua)',
          }}
        >
          {d.ma}
        </span>
        <span
          className={
            dangThem
              ? 'the the-nhan'
              : d.trang_thai === 'DA_HUY'
                ? 'the the-mo'
                : d.trang_thai === 'DA_HOAN'
                  ? 'the the-cho'
                  : 'the the-tot'
          }
        >
          {dangThem
            ? 'Đang thêm'
            : d.trang_thai === 'DA_HUY'
              ? 'Đã huỷ'
              : d.trang_thai === 'DA_HOAN'
                ? 'Đơn hoàn'
                : 'Hoàn thành'}
        </span>
        {treoLau && <span className="the the-cho the-gap">Treo lâu</span>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--chu-2)', display: 'flex', gap: 10, alignItems: 'baseline' }}>
        {/* Người MỞ giỏ. Từ 04/09 không còn ai "đang giữ" — nhiều người cùng
            thêm vào một giỏ, nên chỉ tên người mở là ổn định.

            Tên dài thì CẮT BỚT TÊN, không bao giờ ép số tiền co lại. */}
        <span
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
        >
          {d.nguoi_tao.ho_ten}
        </span>
        <span style={{ color: 'var(--chu-3)', flexShrink: 0 }}>{d.dong.length} món</span>
        <span
          style={{
            color: treoLau ? 'var(--cho)' : 'var(--chu-3)',
            fontWeight: treoLau ? 600 : 400,
            flexShrink: 0,
          }}
        >
          {truocDay(d.them_hang_lan_cuoi ?? d.tao_luc)}
        </span>
        <span className="tien" style={{ marginLeft: 'auto', fontSize: 16, flexShrink: 0 }}>
          {tien(d.tien.tong)}
        </span>
      </div>
    </button>
  );
}

// ───────────────────────── chi tiết ─────────────────────────

function ChiTiet({
  don,
  song,
  xong,
  hep = false,
  quayLai,
}: {
  don: Don | null;
  song: boolean;
  xong: () => void;
  /** Điện thoại: chi tiết chiếm cả màn, có nút quay lại hàng đợi, lề và chữ nhỏ hơn. */
  hep?: boolean;
  quayLai?: () => void;
}) {
  const [hoiLai, datHoiLai] = useState<'xac-nhan' | 'huy' | null>(null);
  const [themHang, datThemHang] = useState(false);
  const [moGhiChu, datMoGhiChu] = useState(false);
  const [dangLam, datDangLam] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);

  useEffect(() => {
    datHoiLai(null);
    datLoi(null);
  }, [don?.id]);

  if (!don) {
    return (
      <div
        className="noi troi-len"
        style={{
          flex: 1,
          display: 'grid',
          placeItems: 'center',
          color: 'var(--chu-3)',
          padding: 30,
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {hep && quayLai && (
          <button onClick={quayLai} style={{ position: 'absolute', top: 14, left: 14, padding: '9px 14px' }}>
            ← Đơn hàng
          </button>
        )}
        <div className="hien-len">
          <div
            className="gieng"
            style={{
              width: 92,
              height: 92,
              fontSize: 40,
              fontWeight: 800,
              color: 'var(--chu-3)',
              margin: '0 auto',
            }}
          >
            ₫
          </div>
          <div style={{ marginTop: 20 }}>{hep ? 'Đang mở đơn…' : 'Chọn một giỏ bên trái để xem chi tiết và thu tiền'}</div>
        </div>
      </div>
    );
  }

  const lam = async (viec: 'xac-nhan' | 'huy') => {
    datDangLam(true);
    datLoi(null);
    try {
      await goi(`/api/don/${don.id}/${viec}`, { method: 'POST' });
      datHoiLai(null);
      // KHÔNG bỏ chọn: đơn vừa xác nhận vẫn nằm trong cột trái (mục "hoàn
      // thành"), và thu ngân thường muốn nhìn lại nó ngay sau khi bấm.
      xong();
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không gọi được máy chủ.');
      datHoiLai(null);
    } finally {
      datDangLam(false);
    }
  };

  const moDuoc = don.trang_thai === 'DANG_MUA';
  const coGiam = don.tien.giam_tung_dong + don.tien.giam_ca_don > 0;

  return (
    // `key` theo id đơn: đổi giỏ thì cả khối vào lại từ đầu, nên mắt thấy rõ
    // "đây là một đơn khác" thay vì mấy con số lặng lẽ đổi tại chỗ.
    <div
      key={don.id}
      className="noi hien-mo"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: hep ? '14px 16px 10px' : '22px 28px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: hep ? 12 : 18,
          flexShrink: 0,
        }}
      >
        {hep && quayLai && (
          <button onClick={quayLai} aria-label="Về hàng đợi" style={{ padding: '10px 13px', flexShrink: 0, fontWeight: 700 }}>
            ←
          </button>
        )}
        <span
          className="so hien-len"
          style={{ fontSize: hep ? 30 : 40, fontWeight: 800, letterSpacing: '0.01em', lineHeight: 1 }}
        >
          {don.ma}
        </span>
        <div className="hien-len" style={{ animationDelay: '60ms', lineHeight: 1.5 }}>
          <div style={{ fontSize: 13, color: 'var(--chu-2)' }}>
            {don.nguoi_tao.ho_ten} · mở lúc {gio(don.tao_luc)}
          </div>
          {don.so_lan_tra_lai > 0 && (
            <span className="the the-cho" style={{ marginTop: 5 }}>
              Đã trả lại {don.so_lan_tra_lai} lần
            </span>
          )}
        </div>

        {/*
          Thu ngân thêm hàng thẳng vào giỏ (mở 04/09).

          Backend vốn đã cho phép từ đầu — `kiemQuyenSua` chỉ chặn theo trạng thái
          giỏ, không chặn theo vai. Nhưng màn hình thì chưa có nút nào, nên câu lỗi
          nhân viên nhận được ("nhờ thu ngân thêm giúp") là một lời hứa suông.
          Đây là chỗ thực hiện lời hứa đó.
        */}
        {moDuoc && (
          <button
            className="nut-chinh"
            style={{ marginLeft: 'auto', flexShrink: 0, padding: hep ? '11px 14px' : '13px 20px' }}
            disabled={!song}
            onClick={() => datThemHang(true)}
          >
            {hep ? '+ Thêm' : '+ Thêm sản phẩm'}
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: hep ? '2px 14px 12px' : '4px 22px 18px' }}>
        {don.dong.length === 0 && (
          <div style={{ color: 'var(--chu-3)', padding: '28px 6px' }}>Giỏ chưa có món nào.</div>
        )}
        {don.dong.map((d, i) =>
          hep ? (
            /*
              Điện thoại: HAI HÀNG. Hàng trên ảnh + tên + đơn giá; hàng dưới số
              lượng · nút sửa · thành tiền. Bản một hàng (máy bàn) ở 390px chỉ
              chừa cho tên ~60px, mỗi chữ một dòng — đã thấy trên Pixel thật.
            */
            <div
              key={d.id}
              className="noi-nho truot-phai"
              style={{ animationDelay: `${Math.min(i, 10) * 40}ms`, padding: '12px 14px', marginBottom: 10 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <AnhSanPham anh={d.anh} ten={d.ten} co={56} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, lineHeight: 1.35 }}>{d.ten}</div>
                  {/* Điện thoại: KHÔNG hiện mã hãng · mã vạch (chủ dự án chốt 15/09) —
                      chỉ bản máy bàn bên dưới có. */}
                  <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 3 }}>
                    {tien(d.don_gia)}/{d.don_vi}
                    {d.giam > 0 && (
                      <span style={{ color: 'var(--nhan)', fontWeight: 600 }}> · giảm {tien(d.giam)}</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                <span
                  className="gieng so"
                  style={{ width: 40, height: 40, fontSize: 17, fontWeight: 700, color: 'var(--chu)' }}
                >
                  {d.so_luong}
                </span>
                {moDuoc && (
                  <SuaDong
                    donId={don.id}
                    dongId={d.id}
                    soLuong={d.so_luong}
                    song={song}
                    xong={xong}
                    datLoi={datLoi}
                  />
                )}
                <span className="tien" style={{ fontSize: 'var(--chu-to)', flexShrink: 0, marginLeft: 'auto' }}>
                  {tien(d.thanh_tien)}
                </span>
              </div>
            </div>
          ) : (
          <div
            key={d.id}
            className="noi-nho truot-phai"
            style={{
              animationDelay: `${Math.min(i, 10) * 40}ms`,
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '13px 18px',
              marginBottom: 10,
            }}
          >
            <AnhSanPham anh={d.anh} ten={d.ten} co={56} />
            {/* Số lượng nằm trong một giếng tròn — mảnh mượn thẳng từ ảnh mẫu,
                và nó tách con số ra khỏi tên hàng mà không cần một nét kẻ nào. */}
            <span
              className="gieng so"
              style={{ width: 46, height: 46, fontSize: 19, fontWeight: 700, color: 'var(--chu)' }}
            >
              {d.so_luong}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{d.ten}</div>
              {/* Mã hãng · mã vạch dưới tên, CHỈ máy bàn (15/09): phiếu là chỗ khách
                  đối chiếu; điện thoại chật nên không hiện. */}
              <MaDoiChieu sapcode={d.sapcode} barcode={d.barcode} />
              <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 3 }}>
                {d.don_vi} · {tien(d.don_gia)}
                {d.giam > 0 && (
                  <span style={{ color: 'var(--nhan)', fontWeight: 600 }}> · giảm {tien(d.giam)}</span>
                )}
              </div>
            </div>
            <span className="tien" style={{ fontSize: 'var(--chu-to)', flexShrink: 0 }}>
              {tien(d.thanh_tien)}
            </span>
            {moDuoc && (
              <SuaDong
                donId={don.id}
                dongId={d.id}
                soLuong={d.so_luong}
                song={song}
                xong={xong}
                datLoi={datLoi}
              />
            )}
          </div>
          ),
        )}

        {/* Ghi chú của khách. Giỏ còn mở thì thu ngân sửa được (14/09) — khách dặn
            thêm ở quầy là chuyện thường; đơn đã đóng thì chỉ đọc. */}
        {(don.ghi_chu || don.sua_duoc) && (
          <div
            className="lom-nho hien-len"
            style={{ marginTop: 18, padding: '14px 18px', fontSize: 'var(--chu-nho)', display: 'flex', gap: 12, alignItems: 'flex-start' }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nhan-chu" style={{ marginBottom: 6 }}>
                Ghi chú
              </div>
              {don.ghi_chu ? (
                <div style={{ whiteSpace: 'pre-wrap' }}>{don.ghi_chu}</div>
              ) : (
                <span style={{ color: 'var(--chu-3)' }}>Chưa có — khách dặn gì thêm thì ghi vào đây.</span>
              )}
            </div>
            {don.sua_duoc && (
              <button
                onClick={() => datMoGhiChu(true)}
                disabled={!song}
                style={{ padding: '8px 13px', fontSize: 12, flexShrink: 0 }}
              >
                {don.ghi_chu ? 'Sửa' : 'Ghi chú'}
              </button>
            )}
          </div>
        )}
        {don.trang_thai === 'DA_HOAN' && don.hoan && (
          <div className="lom-nho hien-len" style={{ marginTop: 14, padding: '14px 18px', fontSize: 'var(--chu-nho)', color: 'var(--cho)' }}>
            <div className="nhan-chu" style={{ marginBottom: 6, color: 'var(--cho)' }}>
              Đơn hoàn — khách đã trả hàng
            </div>
            <div style={{ color: 'var(--chu)' }}>{don.hoan.ly_do}</div>
            <div style={{ fontSize: 12, color: 'var(--chu-3)', marginTop: 4 }}>
              {don.hoan.boi?.ho_ten}{don.hoan.luc && ` · ${gio(don.hoan.luc)}`} · hàng đã về kệ, tiền trừ khỏi ngày {don.ngay}
            </div>
          </div>
        )}
      </div>
      {moGhiChu && (
        <HopGhiChu
          gio={don}
          dong={() => datMoGhiChu(false)}
          xong={() => {
            datMoGhiChu(false);
            xong();
          }}
        />
      )}

      <div style={{ padding: hep ? '4px 14px 14px' : '4px 22px 22px', flexShrink: 0 }}>
        {coGiam && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              color: 'var(--chu-2)',
              marginBottom: 10,
              padding: '0 6px',
              fontSize: 'var(--chu-nho)',
            }}
          >
            <span>
              Tạm tính {tien(don.tien.truoc_giam)} · giảm{' '}
              {don.tien.giam_ca_don > 0 ? 'cả đơn' : 'theo sản phẩm'}
            </span>
            <span className="so" style={{ color: 'var(--nhan)', fontWeight: 600 }}>
              − {tien(don.tien.giam_tung_dong + don.tien.giam_ca_don)}
            </span>
          </div>
        )}

        {/*
          Số tiền nằm trong một giếng LÕM — nó là màn hiển thị, không phải nút.
          Chữ dùng class `.tien`: gần như đen, tương phản ~13:1. Ở phong cách
          tương phản thấp này đó là ngoại lệ có chủ ý, xem đầu `nen.css`.
        */}
        <div
          className="lom"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            padding: hep ? '14px 16px' : '18px 22px',
            marginBottom: hep ? 12 : 16,
          }}
        >
          <span className="nhan-chu">Khách phải trả</span>
          {/* KHÔNG chạy số ở đây — thu ngân đọc con số này lên thành tiếng. */}
          <span className="tien" style={{ fontSize: hep ? 26 : 'var(--chu-rat-to)', fontWeight: 800 }}>
            {tien(don.tien.tong)}
          </span>
        </div>

        {loi && (
          <div
            role="alert"
            className="lom-nho hien-len"
            style={{
              marginBottom: 14,
              padding: '13px 16px',
              color: 'var(--nhan-dam)',
              fontSize: 'var(--chu-nho)',
              fontWeight: 600,
            }}
          >
            {loi}
          </div>
        )}

        {/*
          🔴 KHÔNG còn dải "nhân viên chưa gửi giỏ này" (bỏ 04/09).

             Bước gửi đã bỏ, nên "đang thêm" là trạng thái BÌNH THƯỜNG của mọi
             đơn cho tới lúc xác nhận — cảnh báo ở đây sẽ hiện trên gần như mọi
             đơn, và một cảnh báo luôn hiện thì không còn là cảnh báo.
        */}
        <div style={{ display: 'flex', gap: hep ? 10 : 12 }}>
          <button
            className="nut-chinh"
            style={{ flex: 1, padding: hep ? 15 : 18, fontSize: hep ? 'var(--chu-vua)' : 'var(--chu-to)' }}
            disabled={!song || dangLam || !moDuoc || don.dong.length === 0}
            onClick={() => datHoiLai('xac-nhan')}
          >
            {hep ? 'XÁC NHẬN' : 'XÁC NHẬN ĐƠN HÀNG'}
          </button>
          <button
            className="nut-nguy"
            style={{ padding: hep ? '15px 16px' : '18px 24px' }}
            disabled={!song || dangLam || !moDuoc}
            onClick={() => datHoiLai('huy')}
          >
            Huỷ giỏ
          </button>
        </div>
      </div>

      {themHang && (
        <HopThemHang
          donId={don.id}
          dong={() => datThemHang(false)}
          daThem={xong}
        />
      )}

      {hoiLai && (
        <HopHoiLai
          don={don}
          viec={hoiLai}
          dangLam={dangLam}
          dong={() => datHoiLai(null)}
          dongY={() => lam(hoiLai)}
        />
      )}
    </div>
  );
}

/**
 * Popup hỏi lại.
 *
 * Đã chốt từ lượt 1: xác nhận thu tiền là hành động **không quay lui được ở mức
 * nghiệp vụ** — hàng trừ khỏi kho, doanh thu ghi sổ. Popup chặn cái nhầm dễ nhất;
 * cái nhầm nặng thì admin trả lại đơn, nhưng khi đó có người chịu trách nhiệm.
 */
// ───────────────────────── sửa hàng trong giỏ ─────────────────────────

/**
 * Nút bớt / thêm / bỏ cho một dòng, ngay trong tấm chi tiết của thu ngân.
 *
 * 🔴 Xuống 0 là BỎ DÒNG và nhả hàng về kho ngay — nên ở mức 1 thì đổi hẳn ký
 *    hiệu sang thùng rác. Cùng cách làm với màn tư vấn; nút "−" trông vô hại,
 *    còn cái làm món biến mất thì phải nhìn ra là nó làm món biến mất.
 */
function SuaDong({
  donId,
  dongId,
  soLuong,
  song,
  xong,
  datLoi,
}: {
  donId: number;
  dongId: number;
  soLuong: number;
  song: boolean;
  xong: () => void;
  datLoi: (s: string | null) => void;
}) {
  const [dangSua, datDangSua] = useState(false);

  const dat = async (sl: number) => {
    if (dangSua || !song) return;
    datDangSua(true);
    datLoi(null);
    try {
      await goi(`/api/gio/${donId}/dong/${dongId}`, { method: 'PATCH', than: { so_luong: sl } });
      xong();
    } catch (e) {
      // Hết hàng trả 409 kèm số còn lại — hiện nguyên câu của server.
      datLoi(e instanceof LoiApi ? e.message : 'Không sửa được dòng này.');
    } finally {
      datDangSua(false);
    }
  };

  const nut: React.CSSProperties = {
    width: 38,
    height: 38,
    padding: 0,
    fontSize: 17,
    fontWeight: 700,
    borderRadius: 'var(--tron)',
  };

  return (
    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
      <button
        onClick={() => void dat(soLuong - 1)}
        disabled={dangSua || !song}
        aria-label={soLuong === 1 ? 'Bỏ món này' : 'Bớt một'}
        style={nut}
      >
        {soLuong === 1 ? '🗑' : '−'}
      </button>
      <button
        onClick={() => void dat(soLuong + 1)}
        disabled={dangSua || !song}
        aria-label="Thêm một"
        style={nut}
      >
        +
      </button>
    </div>
  );
}

/**
 * Hộp tìm và thêm sản phẩm vào giỏ.
 *
 * 🔴 Treo thẳng vào `document.body` bằng portal — cùng lý do với `HopHoiLai`:
 *    tấm chi tiết có `overflow: hidden`, mà theo quy chuẩn thì `overflow` khác
 *    `visible` tạo ra backdrop root, nên `backdrop-filter` chỉ làm mờ được phần
 *    nền nằm trong tấm đó. Đã nhìn thấy thật: nửa màn hình mờ, nửa còn lại nét.
 *
 * 🔴 Hộp KHÔNG tự đóng sau khi thêm. Thu ngân thường thêm mấy món liền nhau
 *    (khách đứng ngay đó đọc thêm); đóng lại sau mỗi món là bắt họ mở lại và gõ
 *    lại từ khoá. Dòng vừa thêm nháy một cái để biết là đã vào.
 */
function HopThemHang({
  donId,
  dong,
  daThem,
}: {
  donId: number;
  dong: () => void;
  daThem: () => void;
}) {
  const [q, datQ] = useState('');
  const [nhomId, datNhomId] = useState<number | null>(null);
  const [kq, datKq] = useState<SanPham[] | null>(null);
  const [dangTim, datDangTim] = useState(false);
  const hep = useHep();
  const [vuaThem, datVuaThem] = useState<number | null>(null);
  const [loi, datLoi] = useState<string | null>(null);
  const { song } = useKetNoi();

  const nhom = useHoiLai<NhomSanPham[]>('/api/nhom', 60_000);

  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dong();
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [dong]);

  // Gõ tới đâu tìm tới đó, chờ 250ms — không thì mỗi chữ cái là một request và
  // kết quả về không đúng thứ tự.
  useEffect(() => {
    if (!q.trim() && nhomId === null) {
      datKq(null);
      return;
    }
    let con = true;
    datDangTim(true);
    const h = setTimeout(async () => {
      try {
        const ts = new URLSearchParams();
        if (q.trim()) ts.set('q', q.trim());
        if (nhomId !== null) ts.set('nhom_id', String(nhomId));
        const d = await goi<TrangSanPham>(`/api/san-pham?${ts}`);
        if (con) datKq(d.danh_sach);
      } catch (e) {
        if (con) datLoi(e instanceof LoiApi ? e.message : 'Không tìm được.');
      } finally {
        if (con) datDangTim(false);
      }
    }, 250);
    return () => {
      con = false;
      clearTimeout(h);
    };
  }, [q, nhomId]);

  const them = async (sp: SanPham) => {
    if (!song) return;
    datLoi(null);
    try {
      await goi(`/api/gio/${donId}/them`, { method: 'POST', than: { san_pham_id: sp.id, so_luong: 1 } });
      daThem();
      datVuaThem(sp.id);
      setTimeout(() => datVuaThem((c) => (c === sp.id ? null : c)), 900);
    } catch (e) {
      datLoi(e instanceof LoiApi ? e.message : 'Không thêm được.');
    }
  };

  const chip: React.CSSProperties = {
    padding: '8px 15px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 'var(--tron)',
    whiteSpace: 'nowrap',
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={dong}
      className="hien-mo"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(126, 120, 128, 0.46)',
        backdropFilter: 'blur(4px)',
        display: 'grid',
        placeItems: 'center',
        padding: 20,
        zIndex: 50,
      }}
    >
      <div
        className="noi phong-len"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 620,
          maxHeight: '86vh',
          boxShadow: 'var(--noi-to)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '26px 28px 12px', flexShrink: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 14 }}>Thêm sản phẩm</div>
          <input
            value={q}
            onChange={(e) => datQ(e.target.value)}
            placeholder="Tên hàng hoặc mã — gõ không dấu cũng ra"
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            aria-label="Tìm hàng"
          />
          <div style={{ display: 'flex', gap: 9, marginTop: 12, overflowX: 'auto', paddingBottom: 4 }}>
            <button
              onClick={() => datNhomId(null)}
              className={nhomId === null ? 'lom-nho' : undefined}
              style={{ ...chip, color: nhomId === null ? 'var(--nhan)' : undefined }}
            >
              Tất cả
            </button>
            {(nhom.duLieu ?? [])
              .filter((n) => n.active)
              .map((n) => (
                <button
                  key={n.id}
                  onClick={() => datNhomId((c) => (c === n.id ? null : n.id))}
                  className={nhomId === n.id ? 'lom-nho' : undefined}
                  style={{ ...chip, color: nhomId === n.id ? 'var(--nhan)' : undefined }}
                >
                  {n.name}
                </button>
              ))}
          </div>
          {loi && (
            <div
              role="alert"
              className="lom-nho hien-len"
              style={{
                marginTop: 12,
                padding: '11px 15px',
                color: 'var(--nhan-dam)',
                fontSize: 'var(--chu-nho)',
                fontWeight: 600,
              }}
            >
              {loi}
            </div>
          )}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 26px 18px' }}>
          {kq === null && (
            <div style={{ padding: '40px 10px', textAlign: 'center', color: 'var(--chu-3)', lineHeight: 1.7 }}>
              Gõ tên hàng, hoặc chọn một nhóm ở trên.
            </div>
          )}
          {kq !== null && kq.length === 0 && !dangTim && (
            <div style={{ padding: '40px 10px', textAlign: 'center', color: 'var(--chu-3)' }}>
              Không có mặt hàng nào khớp.
            </div>
          )}
          {(kq ?? []).map((sp, i) => (
            <button
              key={sp.id}
              onClick={() => void them(sp)}
              disabled={sp.het_hang || !song}
              className={`truot-phai ${vuaThem === sp.id ? 'lom-nho' : 'noi-nho'}`}
              style={{
                animationDelay: `${Math.min(i, 8) * 30}ms`,
                width: '100%',
                display: 'block',
                textAlign: 'left',
                padding: '13px 16px',
                marginBottom: 10,
              }}
            >
              {/*
                Hai hàng, giống hệt dòng kết quả ở màn tư vấn (14/09). Bản một hàng
                nhét ảnh · tên · thẻ còn hàng · giá cạnh nhau: ở 390px tên chỉ còn
                một chữ mỗi dòng và cột mã bị bẻ thành sáu dòng — đã thấy trên
                điện thoại thật. Tên được cả bề ngang; thẻ và nút xuống hàng dưới.
              */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <AnhSanPham anh={sp.anh} ten={sp.ten} co={hep ? 72 : 88} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Chỉ tên — cùng quyết định 14/09 với màn tư vấn, xem chú thích ở đó.
                      Tìm theo sku/mã hãng/mã vạch vẫn ra, chỉ không in mã. */}
                  <div style={{ fontWeight: 600, lineHeight: 1.35 }}>{sp.ten}</div>
                </div>
                {/* Cột phải: giá · còn bán · + THÊM xếp dọc, cao bằng khung ảnh 88px —
                    không còn hàng trống dưới ảnh (chủ dự án chỉ ra 14/09 tối). */}
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <span className="tien">{tien(sp.gia)}</span>
                  {sp.het_hang ? (
                    <span className="the the-nhan">Hết hàng</span>
                  ) : (
                    <span className="the the-mo">
                      Còn {sp.con_ban} {sp.don_vi}
                    </span>
                  )}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: vuaThem === sp.id ? 'var(--tot)' : sp.het_hang ? 'var(--chu-3)' : 'var(--nhan)',
                  }}
                >
                  {vuaThem === sp.id ? '✓ ĐÃ THÊM' : sp.het_hang ? '' : '+ THÊM'}
                </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div
          style={{
            padding: '14px 28px 24px',
            flexShrink: 0,
            boxShadow: '0 -10px 14px -10px var(--toi)',
          }}
        >
          <button className="nut-chinh" style={{ width: '100%', padding: 15 }} onClick={dong}>
            XONG
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function HopHoiLai({
  don,
  viec,
  dangLam,
  dong,
  dongY,
}: {
  don: Don;
  viec: 'xac-nhan' | 'huy';
  dangLam: boolean;
  dong: () => void;
  dongY: () => void;
}) {
  const thu = viec === 'xac-nhan';

  // Escape để thoát — thu ngân đang vội thì tay ở bàn phím nhanh hơn ở chuột.
  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !dangLam) dong();
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [dong, dangLam]);

  /*
   * 🔴 Hộp thoại phải treo thẳng vào `document.body`, KHÔNG nằm trong tấm chi
   *    tiết.
   *
   *    Tấm chi tiết có `overflow: hidden` (để nội dung không tràn ra ngoài góc
   *    bo tròn). Theo quy chuẩn, một phần tử `overflow` khác `visible` tạo ra
   *    **backdrop root** — nên `backdrop-filter` của màn che chỉ lấy được phần
   *    nền NẰM TRONG tấm đó. Kết quả nhìn thấy thật: nửa phải màn hình mờ, nửa
   *    trái vẫn nét, trông như lỗi hiển thị.
   *
   *    Đưa ra ngoài body thì màn che lấy đúng cả trang.
   */
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onClick={dong}
      className="hien-mo"
      style={{
        position: 'fixed',
        inset: 0,
        // Màn che XÁM chứ không đen: nền vốn đã sáng, một lớp đen 42% biến cả
        // mặt bàn thành xám bùn và mấy cái bóng mềm chết hẳn.
        background: 'rgba(126, 120, 128, 0.46)',
        backdropFilter: 'blur(4px)',
        display: 'grid',
        placeItems: 'center',
        padding: 20,
        zIndex: 50,
      }}
    >
      <div
        className="noi phong-len"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 440, padding: 32, boxShadow: 'var(--noi-to)' }}
      >
        <div style={{ fontSize: 21, fontWeight: 700, marginBottom: 12 }}>
          {thu ? 'Xác nhận đơn hàng này?' : 'Huỷ giỏ này?'}
        </div>
        <div style={{ color: 'var(--chu-2)', marginBottom: 26, lineHeight: 1.65 }}>
          {thu ? (
            <>
              Giỏ <b className="so">{don.ma}</b> — khách trả{' '}
              <b className="tien">{tien(don.tien.tong)}</b>. Hàng sẽ trừ khỏi kho và doanh thu
              ghi sổ ngay.
            </>
          ) : (
            <>
              Giỏ <b className="so">{don.ma}</b> của{' '}
              <b style={{ color: 'var(--chu)' }}>
                {don.nguoi_tao.ho_ten}
              </b>{' '}
              sẽ bị huỷ, toàn bộ hàng nhả về kho. Hỏi họ trước khi huỷ.
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button style={{ flex: 1, padding: 15 }} onClick={dong} disabled={dangLam}>
            Quay lại
          </button>
          <button
            className={thu ? 'nut-chinh' : 'nut-nguy'}
            style={{ flex: 1, padding: 15 }}
            onClick={dongY}
            disabled={dangLam}
          >
            {dangLam ? 'Đang xử lý…' : thu ? 'XÁC NHẬN' : 'Huỷ giỏ'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
